import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Calculator, Package, Wrench, FlaskConical, Zap, Boxes } from "lucide-react";
import { toast, Toaster } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () => todayStr().slice(0, 8) + "01";
const inr = (n) => `₹ ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`;

export default function CostSheet() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [range, setRange] = useState({ start: monthStart(), end: todayStr() });
  const [extras, setExtras] = useState({ manpower: 0, rent: 0 });
  const [sheet, setSheet] = useState(null);

  const compute = useCallback((m, r, ex) => {
    if (!m) { setSheet(null); return; }
    api.get("/cost-sheet", { params: { model: m, start: r.start, end: r.end, manpower_per_bottle: +ex.manpower || 0, rent_per_bottle: +ex.rent || 0 } })
      .then((res) => setSheet(res.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    api.get("/finished-goods").then((res) => {
      setModels(res.data);
      if (res.data.length > 0) { setModel(res.data[0].name); }
    });
  }, []);

  useEffect(() => { if (model) compute(model, range, extras); }, [model, range, extras, compute]);

  const buckets = sheet ? [
    { label: "Raw Material (BOM)", value: sheet.material.total, icon: Package, color: "text-blue-600" },
    { label: "Labor", value: sheet.labor_total, icon: Wrench, color: "text-emerald-600" },
    { label: "Consumables", value: sheet.consumable_total, icon: Boxes, color: "text-amber-600" },
    { label: `Electrolysis${sheet.electrolysis_batch ? ` (${sheet.electrolysis_batch})` : ""}`, value: sheet.electrolysis_per_bottle, icon: FlaskConical, color: "text-purple-600" },
    { label: "Power (allocated)", value: sheet.power.per_bottle, icon: Zap, color: "text-orange-500" },
    { label: "Manpower (editable)", value: sheet.manpower_per_bottle, icon: Wrench, color: "text-pink-600" },
    { label: "Rent (editable)", value: sheet.rent_per_bottle, icon: Boxes, color: "text-slate-600" },
  ] : [];

  return (
    <Layout title="Cost Sheet" subtitle="Accurate per-bottle cost across material, labor, electrolysis & power">
      <Toaster richColors position="top-right" />
      <div className="section-card mb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Finished Good</label>
          <select data-testid="cs-model-select" value={model} onChange={(e) => setModel(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {models.length === 0 && <option value="">No items</option>}
            {models.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">From (for power allocation)</label><input data-testid="cs-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">To</label><input data-testid="cs-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Manpower / bottle (₹)</label><input data-testid="cs-manpower" type="number" step="0.01" value={extras.manpower} onChange={(e) => setExtras({ ...extras, manpower: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Rent / bottle (₹)</label><input data-testid="cs-rent" type="number" step="0.01" value={extras.rent} onChange={(e) => setExtras({ ...extras, rent: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
      </div>

      {!sheet && <div className="section-card text-center py-12 text-slate-400">Select a model to compute its cost sheet.</div>}

      {sheet && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="section-card">
              <div className="flex items-center gap-2 mb-4"><Calculator size={18} className="text-blue-600" /><h3 className="font-heading font-semibold">Cost Breakdown · per bottle</h3></div>
              <div className="space-y-2" data-testid="cs-buckets">
                {buckets.map((b) => (
                  <div key={b.label} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <span className="flex items-center gap-2 text-slate-700"><b.icon size={16} className={b.color} />{b.label}</span>
                    <span className="font-mono font-semibold">{inr(b.value)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="section-card">
              <h3 className="font-heading font-semibold mb-3">Material Components</h3>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase"><tr><th className="text-left py-2 px-3">Component</th><th className="text-right py-2 px-3">Qty/Bottle</th><th className="text-right py-2 px-3">Unit Cost</th><th className="text-right py-2 px-3">Cost</th></tr></thead>
                <tbody>
                  {sheet.material.lines.length === 0 && <tr><td colSpan="4" className="text-center py-6 text-slate-400">No BOM components. Add them in Model BOM.</td></tr>}
                  {sheet.material.lines.map((l, i) => (
                    <tr key={l.component || i} className="border-t border-slate-100">
                      <td className="py-2 px-3">{l.component}</td>
                      <td className="py-2 px-3 text-right font-mono">{l.qty_per_bottle} {l.base_unit}</td>
                      <td className="py-2 px-3 text-right font-mono">{inr(l.unit_cost)}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold">{inr(l.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-5">
            <div className="kpi-card kpi-accent">
              <div className="text-xs uppercase font-semibold text-slate-500">Total Cost / Bottle</div>
              <div data-testid="cs-total" className="font-heading text-4xl font-bold text-slate-900 mt-2">{inr(sheet.total_cost_per_bottle)}</div>
              <div className="text-xs text-slate-400 mt-2">{sheet.model}</div>
            </div>
            <div className="section-card text-sm space-y-2">
              <h4 className="font-heading font-semibold">Power Allocation</h4>
              <div className="flex justify-between"><span className="text-slate-500">Period power cost</span><span className="font-mono">{inr(sheet.power.period_cost)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Bottles produced</span><span className="font-mono">{sheet.power.bottles_produced?.toLocaleString()}</span></div>
              <div className="flex justify-between font-semibold"><span>Per bottle</span><span className="font-mono">{inr(sheet.power.per_bottle)}</span></div>
              {!sheet.electrolysis_batch && <p className="text-xs text-amber-600 pt-2">No closed electrolysis batch yet — electrolysis cost shows ₹0 until you close a batch.</p>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
